import { useState, type ReactNode } from "react"
import { router } from "@inertiajs/react"
import { Plus, Trash2, Truck } from "lucide-react"

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import { Card, CardAction, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { MainContainer } from "@/components/ui/container"
import {
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
  ResponsiveDrawer,
} from "@/components/ui/drawer"
import { PageHeader } from "@/components/ui/headers"
import { InlineInput } from "@/components/ui/inline-input"
import { InlineSelect } from "@/components/ui/inline-select"
import { Label } from "@/components/ui/label"
import { StatusBadge } from "@/components/ui/status-badge"
import { SupplierForm } from "@/features/staff/purchasing/supplier-form"
import {
  PURCHASING_PATHS,
  tPurchasing,
  type Supplier,
} from "@/features/staff/purchasing/purchasing"
import { t } from "@/lib/i18n"
import { patchInline } from "@/lib/inline-update"

export type SuppliersWorkspaceProps = {
  suppliers: Supplier[]
}

const PAGE_SCOPE = "ui.react.pages.staff.location.manage.suppliers.index"

function InlineField({
  label,
  htmlFor,
  children,
}: {
  label: string
  htmlFor: string
  children: ReactNode
}) {
  return (
    <div className="space-y-1">
      <Label htmlFor={htmlFor} className="text-muted-foreground text-xs">
        {label}
      </Label>
      {children}
    </div>
  )
}

function SupplierCard({ supplier, onDelete }: { supplier: Supplier; onDelete: () => void }) {
  const patchField = (field: string) => (value: string) =>
    patchInline(PURCHASING_PATHS.supplier(supplier.id), { [field]: value })

  return (
    <Card density="compact" data-testid={`supplier-card-${supplier.id}`}>
      <CardHeader>
        <CardTitle className="flex flex-wrap items-center gap-2">
          <span className="truncate">{supplier.name}</span>
          <StatusBadge
            tone={supplier.active ? "available" : "disabled"}
            label={
              supplier.active ? tPurchasing("supplier_active") : tPurchasing("supplier_inactive")
            }
            data-testid={`supplier-state-${supplier.id}`}
          />
        </CardTitle>

        <CardAction className="flex items-center gap-2">
          <InlineSelect
            value={supplier.active ? "true" : "false"}
            options={[
              {
                value: "true",
                label: tPurchasing("supplier_active"),
                testId: `supplier-${supplier.id}-active-option-true`,
              },
              {
                value: "false",
                label: tPurchasing("supplier_inactive"),
                testId: `supplier-${supplier.id}-active-option-false`,
              },
            ]}
            size="sm"
            className="w-32"
            successMessage={tPurchasing("supplier_updated")}
            data-testid={`supplier-${supplier.id}-active`}
            onSubmit={patchField("active")}
          />
          {supplier.destroyable && (
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              onClick={onDelete}
              aria-label={tPurchasing("supplier_delete")}
              data-testid={`supplier-delete-${supplier.id}`}
            >
              <Trash2 className="size-4" />
            </Button>
          )}
        </CardAction>
      </CardHeader>

      <CardContent className="space-y-2">
        <div className="grid gap-4 sm:grid-cols-3">
          <InlineField
            label={tPurchasing("supplier_name_label")}
            htmlFor={`supplier-${supplier.id}-name`}
          >
            <InlineInput
              id={`supplier-${supplier.id}-name`}
              value={supplier.name}
              successMessage={tPurchasing("supplier_updated")}
              data-testid={`supplier-${supplier.id}-name`}
              onSubmit={patchField("name")}
            />
          </InlineField>

          <InlineField
            label={tPurchasing("supplier_contact_label")}
            htmlFor={`supplier-${supplier.id}-contact`}
          >
            <InlineInput
              id={`supplier-${supplier.id}-contact`}
              value={supplier.contactName ?? ""}
              placeholder={tPurchasing("supplier_contact_placeholder")}
              successMessage={tPurchasing("supplier_updated")}
              data-testid={`supplier-${supplier.id}-contact`}
              onSubmit={patchField("contact_name")}
            />
          </InlineField>

          <InlineField
            label={tPurchasing("supplier_phone_label")}
            htmlFor={`supplier-${supplier.id}-phone`}
          >
            <InlineInput
              id={`supplier-${supplier.id}-phone`}
              type="tel"
              inputMode="tel"
              value={supplier.phone ?? ""}
              placeholder={tPurchasing("supplier_phone_placeholder")}
              successMessage={tPurchasing("supplier_updated")}
              data-testid={`supplier-${supplier.id}-phone`}
              onSubmit={patchField("phone")}
            />
          </InlineField>
        </div>

        {!supplier.destroyable && (
          <p
            className="text-muted-foreground text-xs"
            data-testid={`supplier-locked-${supplier.id}`}
          >
            {tPurchasing("supplier_has_purchases", { count: supplier.purchaseCount ?? 0 })}
          </p>
        )}
      </CardContent>
    </Card>
  )
}

/**
 * Who the restaurant buys from.
 *
 * ## Deactivating is the everyday action; deleting is the rare one
 *
 * `Location::Supplier` restricts destroy on its purchases, because a delivery has
 * to keep naming who it came from forever. So the trash icon appears only on a
 * supplier nobody has ever ordered from, and every other row says how many
 * deliveries hold it in place. The switch beside it is the action a manager
 * actually wants: an inactive supplier stops being offered when a purchase is
 * entered and every past delivery keeps its name.
 *
 * ## Everything commits inline
 *
 * A name, a contact and a phone are independent light fields — the "Updates vs
 * creates" rule sends exactly that to `InlineInput`. Only creating keeps a submit
 * button, because a supplier with no name is nothing rather than half of
 * something.
 */
export function SuppliersWorkspace({ suppliers }: SuppliersWorkspaceProps) {
  const [creating, setCreating] = useState(false)
  const [pendingDelete, setPendingDelete] = useState<Supplier | null>(null)

  const confirmDelete = () => {
    if (!pendingDelete) return

    router.delete(PURCHASING_PATHS.supplier(pendingDelete.id), {
      preserveScroll: true,
      onFinish: () => setPendingDelete(null),
    })
  }

  return (
    <MainContainer size="md">
      <div className="space-y-6">
        <PageHeader
          title={t(`${PAGE_SCOPE}.title`)}
          description={t(`${PAGE_SCOPE}.description`)}
          actions={
            suppliers.length > 0 ? (
              <Button type="button" onClick={() => setCreating(true)} data-testid="supplier-new">
                <Plus className="size-4" />
                {tPurchasing("supplier_new")}
              </Button>
            ) : undefined
          }
        />

        {suppliers.length === 0 ? (
          <Card data-testid="suppliers-empty">
            <CardContent className="flex flex-col items-center gap-3 py-10 text-center">
              <Truck className="text-muted-foreground size-8" aria-hidden="true" />
              <p className="text-sm font-semibold">{tPurchasing("suppliers_empty_title")}</p>
              <p className="text-muted-foreground max-w-md text-sm">
                {tPurchasing("suppliers_empty_hint")}
              </p>
              <Button
                type="button"
                onClick={() => setCreating(true)}
                data-testid="supplier-new-empty"
              >
                <Plus className="size-4" />
                {tPurchasing("supplier_new")}
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4" data-testid="suppliers-list">
            {suppliers.map((supplier) => (
              <SupplierCard
                key={supplier.id}
                supplier={supplier}
                onDelete={() => setPendingDelete(supplier)}
              />
            ))}
          </div>
        )}
      </div>

      {creating && (
        <ResponsiveDrawer open onOpenChange={(next) => !next && setCreating(false)}>
          <DrawerContent className="data-[vaul-drawer-direction=right]:sm:max-w-md">
            <DrawerHeader>
              <DrawerTitle>{tPurchasing("supplier_new_title")}</DrawerTitle>
              <DrawerDescription>{tPurchasing("supplier_form_description")}</DrawerDescription>
            </DrawerHeader>

            <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-4">
              <SupplierForm onDone={() => setCreating(false)} />
            </div>
          </DrawerContent>
        </ResponsiveDrawer>
      )}

      <AlertDialog
        open={pendingDelete !== null}
        onOpenChange={(next) => !next && setPendingDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{tPurchasing("supplier_delete_title")}</AlertDialogTitle>
            <AlertDialogDescription>
              {tPurchasing("supplier_delete_description")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="supplier-delete-cancel">
              {tPurchasing("cancel")}
            </AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} data-testid="supplier-delete-confirm">
              {tPurchasing("supplier_delete_confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </MainContainer>
  )
}
